import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warehouse, Plus, Trash2, Search, ArrowRightLeft, Star } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminWarehouses() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [godowns, setGodowns] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [bd, setBd] = useState(null); // { store_total, godowns:[{warehouse_id,name,stock,is_default}] }
  const [addName, setAddName] = useState("");
  const [addCode, setAddCode] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [mv, setMv] = useState({ from_warehouse: "", to_warehouse: "", quantity: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/stores").then((r) => { setStores(r.data); if (r.data[0]) setStoreId(r.data[0].id); }); }, []);
  const loadGodowns = (sid) => api.get(`/admin/warehouses?store_id=${sid}`).then((r) => setGodowns(r.data)).catch(() => {});
  useEffect(() => { if (storeId) { loadGodowns(storeId); setProduct(null); setBd(null); } }, [storeId]);

  const search = async () => {
    if (!q.trim()) return;
    try { const { data } = await api.get(`/products?q=${encodeURIComponent(q)}`); setResults(data.slice(0, 8)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const loadBd = (pid) => api.get(`/admin/warehouse-stock?store_id=${storeId}&product_id=${pid}`).then((r) => setBd(r.data)).catch((e) => toast.error(apiErr(e)));
  const pick = (p) => { setProduct(p); setResults([]); setQ(p.name); loadBd(p.id); };

  const addGodown = async () => {
    if (!addName.trim()) return;
    setBusy(true);
    try { await api.post("/admin/warehouses", { store_id: storeId, name: addName, code: addCode }); toast.success("Godown ban gaya"); setAddName(""); setAddCode(""); loadGodowns(storeId); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const delGodown = async (w) => {
    try { await api.delete(`/admin/warehouses/${w.warehouse_id || w.id}`); toast.success("Godown deleted"); loadGodowns(storeId); if (product) loadBd(product.id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const allocate = async (w, val) => {
    const stock = parseInt(val);
    if (isNaN(stock)) return;
    try { const { data } = await api.post("/admin/warehouse-stock/allocate", { store_id: storeId, product_id: product.id, warehouse_id: w.warehouse_id, stock }); setBd({ ...bd, ...data }); toast.success("Allocated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doMove = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/warehouse-stock/move", { store_id: storeId, product_id: product.id, from_warehouse: mv.from_warehouse, to_warehouse: mv.to_warehouse, quantity: parseInt(mv.quantity) }); setBd({ ...bd, ...data }); setMoveOpen(false); setMv({ from_warehouse: "", to_warehouse: "", quantity: "" }); toast.success("Stock moved between godowns"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-warehouses">
      <div className="flex items-center gap-2 flex-wrap">
        <Warehouse size={22} className="text-indigo-600" />
        <h1 className="text-2xl font-bold">Godowns &amp; Warehouses</h1>
        <select data-testid="wh-store-select" value={storeId} onChange={(e) => setStoreId(e.target.value)} className="ml-auto border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <p className="text-sm text-slate-500 -mt-2">Har store ke andar multiple godown banayein aur stock ko godown-wise distribute karein. Store ka total stock (sales/sync ke liye) same rehta hai.</p>

      {/* Godowns list + add */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="font-bold text-sm mb-3">Godowns is store me ({godowns.length})</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {godowns.map((w) => (
            <div key={w.id} data-testid={`godown-chip-${w.id}`} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
              {w.is_default && <Star size={13} className="text-amber-500 fill-amber-400" />}
              <span className="font-semibold">{w.name}</span>{w.code && <span className="text-[11px] text-slate-400">{w.code}</span>}
              {!w.is_default && <button data-testid={`godown-del-${w.id}`} onClick={() => delGodown(w)} className="text-rose-400 hover:text-rose-600"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <input data-testid="wh-add-name" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Naya godown naam (e.g. Backstore)" className="flex-1 min-w-[160px] border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="wh-add-code" value={addCode} onChange={(e) => setAddCode(e.target.value)} placeholder="Code (opt)" className="w-28 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="wh-add-btn" disabled={busy} onClick={addGodown} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-bold text-sm flex items-center gap-1"><Plus size={16} /> Add Godown</button>
        </div>
      </div>

      {/* Product picker */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="font-bold text-sm mb-3">Product ka godown-wise stock</h2>
        <div className="flex gap-2 relative">
          <input data-testid="wh-product-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Product khojein…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="wh-search-btn" onClick={search} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm flex items-center gap-1"><Search size={16} /> Search</button>
          {results.length > 0 && (
            <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              {results.map((p) => (
                <button key={p.id} data-testid={`wh-result-${p.id}`} onClick={() => pick(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-50 last:border-0">
                  <span className="font-semibold">{p.name}</span> <span className="text-[11px] text-slate-400">{p.sku}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {product && bd && (
          <div className="mt-4" data-testid="wh-breakdown">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm">Store total: <span className="font-extrabold" data-testid="wh-store-total">{bd.store_total}</span> units</p>
              {bd.godowns.length > 1 && <button data-testid="wh-move-btn" onClick={() => setMoveOpen(true)} className="text-sm font-semibold text-indigo-600 flex items-center gap-1"><ArrowRightLeft size={14} /> Move between godowns</button>}
            </div>
            <div className="space-y-2">
              {bd.godowns.map((w) => (
                <div key={w.warehouse_id} data-testid={`wh-row-${w.warehouse_id}`} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {w.is_default && <Star size={13} className="text-amber-500 fill-amber-400 shrink-0" />}
                    <span className="font-semibold text-sm truncate">{w.name}</span>
                    {w.is_default && <span className="text-[10px] text-slate-400">(remainder)</span>}
                  </div>
                  {w.is_default ? (
                    <span className="font-extrabold text-slate-700" data-testid={`wh-stock-${w.warehouse_id}`}>{w.stock}</span>
                  ) : (
                    <input data-testid={`wh-alloc-${w.warehouse_id}`} type="number" defaultValue={w.stock} onBlur={(e) => allocate(w, e.target.value)} className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-right font-bold" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Non-default godown me number likh kar (blur) allocate karein. Default godown baaki stock auto rakhta hai.</p>
          </div>
        )}
      </div>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="wh-move-modal">
          <DialogHeader><DialogTitle>Move stock between godowns</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <select data-testid="wh-move-from" value={mv.from_warehouse} onChange={(e) => setMv({ ...mv, from_warehouse: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm">
              <option value="">From godown…</option>{bd?.godowns.map((w) => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name} ({w.stock})</option>)}
            </select>
            <select data-testid="wh-move-to" value={mv.to_warehouse} onChange={(e) => setMv({ ...mv, to_warehouse: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm">
              <option value="">To godown…</option>{bd?.godowns.map((w) => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name} ({w.stock})</option>)}
            </select>
            <input data-testid="wh-move-qty" type="number" value={mv.quantity} onChange={(e) => setMv({ ...mv, quantity: e.target.value })} placeholder="Quantity" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <button data-testid="wh-move-submit" disabled={busy || !mv.from_warehouse || !mv.to_warehouse} onClick={doMove} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-bold">Move Stock</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
